import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  Link as MuiLink,
} from "@mui/material";
// import { useSEO } from "@/lib/seo";
// import { Reveal } from "@/components/site/Reveal";
// import { QuoteForm } from "@/components/site/QuoteForm";
// import { COMPANY } from "@/data/products";
import { useSEO } from "../lib/seo";
import { Reveal } from "../site/Reveal";
import { QuoteForm } from "../components/QuoteForm";
import { COMPANY } from "../data/products";

/**
 * Design tokens pulled from the original Tailwind theme so colors/spacing
 * stay pixel-identical after the MUI refactor.
 */
const COLORS = {
  obsidian: "#0B0B0C",
  ink: "#111114",
  safety: "#FF5A1F",
  white: "rgba(255,255,255,1)",
  whiteMuted: "rgba(255,255,255,0.6)",
  mutedForeground: "#6B7280",
  border: "rgba(17,17,20,0.15)",
};

const FONT_DISPLAY =
  '"Neue Haas Grotesk Display", "Helvetica Neue", Arial, sans-serif';
const FONT_MONO = '"IBM Plex Mono", "SFMono-Regular", Menlo, monospace';

// Reusable "grain" texture overlay to mimic the original .grain / .grain-dark utility classes
const grainOverlaySx = {
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    opacity: 0.05,
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
    mixBlendMode: "overlay",
  },
};

const monoLabelSx = {
  fontFamily: FONT_MONO,
  fontSize: "0.75rem",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 500,
};

export default function Contact() {
  useSEO({
    title: "Contact & Get a Quote | ITL Technical Services",
    description:
      "Request a quote for servo voltage stabilizers, isolation transformers and CVTs. Talk to an engineer at ITL Technical Services for custom-built power conditioning equipment.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact ITL Technical Services",
    },
  });

  const details = [
    {
      icon: Phone,
      label: "Phone",
      value: COMPANY.phone,
      href: `tel:${COMPANY.phone}`,
    },
    {
      icon: Mail,
      label: "Email",
      value: COMPANY.email,
      href: `mailto:${COMPANY.email}`,
    },
    { icon: MapPin, label: "Address", value: COMPANY.address },
    { icon: Clock, label: "Working Hours", value: COMPANY.hours },
  ];

  return (
    <>
      {/* ---- Header ---- */}
      <Box
        data-testid="contact-header"
        component="section"
        sx={{
          position: "relative",
          color: "#fff",
          overflow: "hidden",
          pt: { xs: 16, md: 22 },
          pb: { xs: 8, md: 12 },

          backgroundImage: `
      linear-gradient(
        rgba(10,10,10,0.75),
        rgba(10,10,10,0.82)
      ),
      url("/ContactUsBanner.jpg")
    `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: "1280px",
            mx: "auto",
            px: { xs: 2, sm: 3, lg: 4 },
          }}
        >
          <Typography
            sx={{
              ...monoLabelSx,
              color: COLORS.safety,
              mb: 4,
              textAlign: "left",
            }}
          >
            [ Get a quote ]
          </Typography>

          <Typography
            component={motion.h1}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            sx={{
              fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
              fontWeight: 900,
              fontSize: { xs: "3rem", md: "4.5rem" },
              letterSpacing: "-0.04em",
              lineHeight: 0.85,
              maxWidth: "56rem",
              m: 0,
              textAlign: "left",
            }}
          >
            Talk to an engineer.
          </Typography>

          <Typography
            sx={{
              color: COLORS.whiteMuted,
              mt: 4,
              maxWidth: "36rem",
              fontSize: "1.125rem",
              textAlign: "left",
            }}
          >
            Share your load and site details and we'll respond with a tailored
            quotation.
          </Typography>
        </Container>
      </Box>

      {/* ---- Body ---- */}
      <Container
        maxWidth={false}
        component="section"
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 8, md: 12 },
        }}
      >
        <Grid container spacing={{ xs: 6, md: 6, lg: 10 }}>
          {/* Contact details */}
          <Grid item xs={12} md={5}>
            <Reveal>
              <Typography
                component="h2"
                sx={{
                  fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                  fontWeight: 900,
                  fontSize: "1.875rem",
                  letterSpacing: "-0.03em",
                  color: COLORS.ink,
                  mb: 4,
                  textAlign: "left",
                }}
              >
                Contact details
              </Typography>

              <Stack
                component="ul"
                spacing={4}
                sx={{ listStyle: "none", p: 0, m: 0 }}
              >
                {details.map((d) => (
                  <Stack
                    key={d.label}
                    component="li"
                    direction="row"
                    spacing={2.5}
                    sx={{ alignItems: "flex-start" }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: COLORS.ink,
                        color: COLORS.white,
                      }}
                    >
                      <d.icon size={20} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          ...monoLabelSx,
                          color: COLORS.mutedForeground,
                          mb: 0.5,
                          textAlign: "left",
                        }}
                      >
                        {d.label}
                      </Typography>
                      {d.href ? (
                        <MuiLink
                          href={d.href}
                          underline="none"
                          sx={{
                            color: COLORS.ink,
                            fontWeight: 500,
                            wordBreak: "break-word",
                            transition: "color 0.2s ease",
                            "&:hover": { color: COLORS.safety },
                            textAlign: "left",
                          }}
                        >
                          {d.value}
                        </MuiLink>
                      ) : (
                        <Typography sx={{ color: COLORS.ink, fontWeight: 500 }}>
                          {d.value}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Reveal>
          </Grid>

          {/* Quote form */}
          <Grid item xs={12} md={7}>
            <Reveal delay={0.15}>
              <Box
                sx={{
                  border: `1px solid ${COLORS.border}`,
                  bgcolor: "#FFFFFF",
                  p: { xs: 3, md: 5 },
                }}
              >
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: FONT_DISPLAY,
                    fontWeight: 900,
                    fontSize: "1.875rem",
                    letterSpacing: "-0.03em",
                    color: COLORS.ink,
                    mb: 4,
                    fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                    textAlign: "left",
                  }}
                >
                  Request a quote
                </Typography>
                <QuoteForm />
              </Box>
            </Reveal>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
